import { IconAdd2, IconFolder, IconLoading } from "@icons/index";
import { Upload, Image } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { Trans } from "react-i18next";
import { useState } from "react";
import useUploadAgreement from "@hooks/useUploadAgreement";
import "./index.scss";
import { IMAGE_UPLOAD_CONFIG, useImageUpload } from "../hooks";
import { AnimatedBorder } from "@components/AnimatedBorder";
import classNames from "classnames";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import type { ProductImageResult } from "@apis/upload";

export interface UploadImageProps {
  /** 上传成功回调 */
  onSuccess?: (result: ProductImageResult[]) => void;
  /** 上传失败回调 */
  onError?: (error: unknown) => void;
  /** 添加从库中选择图片回调 */
  onAddFromLibrary?: () => void;
  beforeUpload?: () => void;
}

const SingleImageUpload = ({ onSuccess, onError, onAddFromLibrary, beforeUpload }: UploadImageProps) => {
  // 使用共享的上传 hook
  const { status, setStatus, uploadImage, getBase64 } = useImageUpload({ onSuccess, onError });
  // 上传协议确认
  const { agreementModal, confirmAgreement } = useUploadAgreement();
  // 文件列表状态
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  // 预览状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const isDark = useAtomValue(themeActionAtom) === "dark";

  // 处理预览
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as File);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  // 处理文件列表变化
  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    // 根据文件列表更新状态
    if (newFileList.length === 0) {
      setStatus("idle");
    } else if (newFileList.some((f) => f.status === "uploading")) {
      setStatus("uploading");
    } else if (newFileList.some((f) => f.status === "done")) {
      setStatus("success");
    } else if (newFileList.some((f) => f.status === "error")) {
      setStatus("error");
    }
  };

  const { ACCEPTED_MIME_TYPES, ACCEPTED_EXTENSIONS } = IMAGE_UPLOAD_CONFIG;

  // antd Upload 配置
  const uploadProps: UploadProps = {
    name: "file",
    pastable: true,
    listType: "picture-card",
    fileList,
    maxCount: 1,
    accept: [...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(","),
    onPreview: handlePreview,
    onChange: handleChange,
    beforeUpload: async (file) => {
      beforeUpload?.();
      const confirmed = await confirmAgreement();
      if (!confirmed) {
        return Upload.LIST_IGNORE;
      }
      const result = await uploadImage(file);
      // 失败时不要把文件留在 antd Upload 列表里，避免出现「失败仍展示成功状态」的视觉
      if (!result) {
        return Upload.LIST_IGNORE;
      }
      return false; // 上传成功，显示预览图
    },
  };

  return (
    <AnimatedBorder borderRadius={12} borderWidth={1} duration={8} className="shadow-xl shadow-blue-500/10">
      <div className="w-full h-full bg-kds-bg-element p-[12px] rounded-[12px]">
        <div
          className={classNames(
            "w-[240px] h-[240px] bg-kds-bg-soft rounded-[8px] overflow-hidden flex items-center justify-center transition-all duration-200",
            isDark ? "hover:bg-kds-bg" : "hover:bg-kds-bg-overlay",
          )}
        >
          <Upload {...uploadProps} className="video-upload">
            {fileList.length === 0 && (
              <>
                {/* error 且无预览文件时与 idle 共用占位，避免失败后空白 */}
                {(status === "idle" || status === "error") && (
                  <div className="label flex flex-col items-center gap-[8px] text-center">
                    <div className="flex-center rounded-full bg-kds-bg-element w-[48px] h-[48px]">
                      <IconAdd2 className="w-[30px] h-[30px]" />
                    </div>
                    <div className="text-[14px] text-kds-fg">
                      <Trans>Drag or paste</Trans>
                    </div>
                    <div
                      className="flex items-center gap-[4px] cursor-pointer whitespace-nowrap mt-[4px] underline text-kds-fg-soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddFromLibrary?.();
                      }}
                    >
                      <IconFolder className="w-[16px] h-[16px] text-black" />
                      <Trans>Add from library</Trans>
                    </div>
                  </div>
                )}
                {status === "uploading" && (
                  <div className="flex flex-col items-center gap-[16px] text-kds-fg-soft">
                    <IconLoading className="w-[24px] h-[24px] animate-spin" />
                    <div className="text-[14px]">
                      <Trans>Uploading...</Trans>
                    </div>
                  </div>
                )}
                {status === "success" && (
                  <div className="flex w-full flex-col items-center gap-[16px] text-kds-fg">
                    <div className="text-[14px]">
                      <Trans>Upload successful</Trans>
                    </div>
                    <div className="text-[12px] text-kds-fg-soft">
                      <Trans>Click or drag to re-upload</Trans>
                    </div>
                  </div>
                )}
              </>
            )}
          </Upload>
        </div>
      </div>
      {/* 图片预览弹窗 */}
      {previewImage && (
        <Image
          wrapperStyle={{ display: "none" }}
          preview={{
            open: previewOpen,
            onOpenChange: (open) => setPreviewOpen(open),
          }}
          src={previewImage}
        />
      )}

      {/* 上传协议确认弹窗 */}
      {agreementModal}
    </AnimatedBorder>
  );
};

export default SingleImageUpload;
